import { useMutation } from "@apollo/client/react";
import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";

import {
  UpdateUserOrganizationDocument,
  type UpdateUserOrganizationMutation,
  type UpdateUserOrganizationMutationVariables,
} from "~/features/admin/users/gqls";
import { translateError } from "~/lib/errors";

export interface BulkUserOrganizationTarget {
  userId: string;
  displayName: string;
  email: string;
  currentOrganizationName: string;
}

export interface BulkUserOrganizationFailure extends BulkUserOrganizationTarget {
  message: string;
}

export interface BulkUserOrganizationRunResult {
  succeeded: BulkUserOrganizationTarget[];
  confirmations: BulkUserOrganizationFailure[];
  failures: BulkUserOrganizationFailure[];
}

interface BulkUserOrganizationProgress {
  done: number;
  total: number;
}

export function useBulkUserOrganizationUpdate() {
  const { t } = useTranslation();
  const [running, setRunning] = useState(false);
  const [progress, setProgress] = useState<BulkUserOrganizationProgress | null>(null);
  const [updateOrganization] = useMutation<UpdateUserOrganizationMutation, UpdateUserOrganizationMutationVariables>(
    UpdateUserOrganizationDocument,
    {
      update: (cache, { data }) => {
        if (data?.cnutUpdateUserOrganization.__typename !== "UserUpdateOrganizationOutput") return;

        cache.evict({ id: "ROOT_QUERY", fieldName: "tenants" });
        cache.evict({ id: "ROOT_QUERY", fieldName: "tenantsByPk" });
        cache.gc();
      },
    },
  );

  const run = useCallback(
    async (
      targets: BulkUserOrganizationTarget[],
      organizationShortCode: string,
      removeMembershipsOutsideDestination: boolean,
    ): Promise<BulkUserOrganizationRunResult> => {
      setRunning(true);
      setProgress({ done: 0, total: targets.length });
      const result: BulkUserOrganizationRunResult = {
        succeeded: [],
        confirmations: [],
        failures: [],
      };

      try {
        for (const [index, target] of targets.entries()) {
          try {
            const response = await updateOrganization({
              variables: {
                userId: target.userId,
                organizationShortCode,
                removeMembershipsOutsideDestination,
              },
            });
            const output = response.data?.cnutUpdateUserOrganization;

            if (output?.__typename === "UserUpdateOrganizationOutput") {
              result.succeeded.push(target);
            } else if (output?.__typename === "ValidationError") {
              const failure = { ...target, message: output.message };
              const requiresConfirmation = output.fields.some(
                (field) => field.path === "removeMembershipsOutsideDestination",
              );
              if (!removeMembershipsOutsideDestination && requiresConfirmation) {
                result.confirmations.push(failure);
              } else {
                result.failures.push(failure);
              }
            } else {
              result.failures.push({ ...target, message: t("error.unknown") });
            }
          } catch (error) {
            result.failures.push({ ...target, message: translateError(t, error) });
          }
          setProgress({ done: index + 1, total: targets.length });
        }
      } finally {
        setRunning(false);
      }

      return result;
    },
    [t, updateOrganization],
  );

  return { run, running, progress };
}
